import { Button } from '@/ui';

import type { MobileUtPhonePhase } from './shared-survey-mobile-ut-phone.types';

interface Props {
  phase: MobileUtPhonePhase;
  title: string;
  usesMicrophone: boolean;
  errorMessage: string | null;
  completionRedirectUrl: string | null;
  onStart: () => void;
  onContinue: (url: string) => void;
}

export default function MobileUtPhoneStatus({
  phase,
  title,
  usesMicrophone,
  errorMessage,
  completionRedirectUrl,
  onStart,
  onContinue,
}: Props) {
  return (
    <>
      {phase === 'active' && usesMicrophone && (
        <div
          data-test='mobile-ut-mic-status'
          className='absolute top-3 right-3 z-10 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1'
        >
          <span className='size-2 animate-pulse rounded-full bg-red-500' />
          <span className='text-xs text-white/80'>Mic on</span>
        </div>
      )}

      {phase === 'idle' && (
        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-6 px-6'>
          <h1 className='text-center text-xl font-semibold text-white'>
            {title}
          </h1>
          <p className='max-w-sm text-center text-sm text-gray-400'>
            {usesMicrophone ? (
              <>
                Tap the button below to begin the usability test. Your
                microphone will be used for voice commands.
              </>
            ) : (
              'Tap the button below to begin the usability test.'
            )}
          </p>
          <Button data-test='mobile-ut-start-button' onClick={onStart}>
            Start Test
          </Button>
        </div>
      )}

      {phase === 'starting' && (
        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-4'>
          <div className='size-8 animate-spin rounded-full border-2 border-white/20 border-t-white' />
          <p className='text-sm text-gray-400'>Loading session...</p>
        </div>
      )}

      {phase === 'ended' && (
        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 px-6'>
          <p className='text-center text-sm text-white'>
            User test completed. Thank you for your participation.
          </p>
          {completionRedirectUrl !== null && (
            <Button
              data-test='mobile-ut-continue-button'
              onClick={() => onContinue(completionRedirectUrl)}
            >
              Continue
            </Button>
          )}
        </div>
      )}

      {phase === 'error' && (
        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 px-6'>
          <p className='max-w-sm text-center text-sm text-red-400'>
            {errorMessage || 'Something went wrong.'}
          </p>
          <Button data-test='mobile-ut-retry-button' onClick={onStart}>
            Try Again
          </Button>
        </div>
      )}
    </>
  );
}
